import { useState } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { ReactPortalElementId } from 'core/layouts';
import { colors, overline, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { TabsContent, TabsGroup, VerticalTabsList, VerticalTabTrigger } from 'ui/tabsGroup';
import { DemographicsLocations } from './DemographicsLocations';
import { DemographicsProfileFields } from './DemographicsProfileFields';
import { ContactPropertySelect as DemographicsActions } from './ContactPropertySelect';
import { AudienceInsightsErrorBoundary } from '../../../contexts/errorBoundary';
import {
  CustomDemographicContextProvider,
  ProfileQueryContextProvider,
} from '../../../contexts/demographicInsights';
import { useTranslation } from '../../../hooks';

enum ViewType {
  byProfile = 'byProfile',
  byLocation = 'byLocation',
}

const Layout = styled(TabsGroup)`
  align-items: stretch;
  display: flex;
  column-gap: ${spacing.plusHalf};
  margin: ${spacing.plusHalf} ${spacing.double} 0;
`;

const VerticalTabs = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  row-gap: ${spacing.half};
  width: ${pxToRem(200)};
`;

const TabListLabel = styled.div`
  ${overline}
  color: ${colors.textSecondary};
`;

const Content = styled.div`
  flex: 1 0 auto;
  min-height: ${pxToRem(600)};
`;

export const Demographics = () => {
  const { t } = useTranslation();
  const [viewType, setViewType] = useState<ViewType>(ViewType.byProfile);
  const portalPath = document.getElementById(
    ReactPortalElementId.sementationInsightsToolbarActions,
  );

  return (
    <AudienceInsightsErrorBoundary>
      <ProfileQueryContextProvider>
        <CustomDemographicContextProvider>
          <Layout
            onValueChange={(newTab) => setViewType(newTab as ViewType)}
            orientation={'vertical'}
            value={viewType}
          >
            {portalPath && createPortal(<DemographicsActions />, portalPath)}
            <VerticalTabs>
              <TabListLabel>{t('insights.demographics.tabs.listLabel')}</TabListLabel>
              <VerticalTabsList>
                <VerticalTabTrigger value={ViewType.byProfile}>
                  {t('insights.demographics.tabs.byProfileFields')}
                </VerticalTabTrigger>
                <VerticalTabTrigger value={ViewType.byLocation}>
                  {t('insights.demographics.tabs.byLocation')}
                </VerticalTabTrigger>
              </VerticalTabsList>
            </VerticalTabs>
            <Content>
              <TabsContent value={ViewType.byProfile}>
                <DemographicsProfileFields />
              </TabsContent>
              <TabsContent value={ViewType.byLocation}>
                <DemographicsLocations />
              </TabsContent>
            </Content>
          </Layout>
        </CustomDemographicContextProvider>
      </ProfileQueryContextProvider>
    </AudienceInsightsErrorBoundary>
  );
};
